import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarCheck, ShoppingBag, Sparkles, Users } from "lucide-react";
import { useMemberData } from "../../contexts/MemberDataContext";
import { MEMBER } from "../../constants/testIds";
import { fadeUp } from "../../lib/motion";
import { CheckInCard } from "../../components/member/CheckInCard";

function CrowdMeter({ occupancy }) {
  if (!occupancy) return null;
  const pct = occupancy.capacity ? Math.min(100, Math.round((occupancy.count / occupancy.capacity) * 100)) : 0;
  return (
    <div data-testid={MEMBER.crowdMeter} className="rounded-2xl p-6" style={{ background: "#141414", border: "1px solid rgba(255,255,255,0.1)" }}>
      <p className="text-xs uppercase tracking-[0.2em] font-semibold flex items-center gap-1.5" style={{ color: "#A1A1AA" }}><Users size={12} /> Live right now</p>
      <p className="font-heading text-5xl font-black mt-2">{occupancy.count}<span className="text-lg font-medium" style={{ color: "#A1A1AA" }}> / {occupancy.capacity}</span></p>
      <div className="w-full h-2.5 rounded-full mt-4 overflow-hidden" style={{ background: "#242424" }}>
        <motion.div
          initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="h-full rounded-full bg-gradient-to-r from-cyan-400 to-blue-500"
        />
      </div>
    </div>
  );
}

// UX-MEM-01 §1 SCR-P03-01, NFR-070 (single bundled `/member/home` call, no N+1), highest-risk
// rule "crowd-meter must stay calm/offline-safe, never alarming" — the cyan/blue gradient never
// changes color with load (per design_guidelines.json), and the whole card just doesn't render
// if occupancy is null (crowdsense module off, or no home branch yet) rather than erroring.
export default function Home() {
  const { home } = useMemberData();
  const reducedMotion = useReducedMotion();
  const navigate = useNavigate();

  return (
    <div className="space-y-5">
      <motion.div {...fadeUp(reducedMotion)}><CheckInCard /></motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.03)}><CrowdMeter occupancy={home?.occupancy} /></motion.div>

      {home?.nudge && (
        <motion.div {...fadeUp(reducedMotion, 0.05)} data-testid={MEMBER.offPeakCard} className="rounded-2xl p-5 flex items-start gap-3" style={{ background: "#1E1E1E" }}>
          <Sparkles size={18} color="#22D3EE" className="shrink-0 mt-0.5" strokeWidth={1.5} />
          <p className="text-sm" style={{ color: "#D4D4D8" }}>{home.nudge.message}</p>
        </motion.div>
      )}

      {home?.membership && (
        <motion.button
          {...fadeUp(reducedMotion, 0.1)} onClick={() => navigate("/member/membership")}
          data-testid={MEMBER.membershipBanner} className="w-full text-left rounded-2xl p-5 active:scale-95 transition-transform"
          style={{ background: "#141414", border: "1px solid rgba(255,255,255,0.1)" }}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: "#A1A1AA" }}>Membership</span>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: home.membership.status === "active" ? "rgba(50,215,75,0.15)" : "rgba(214, 32, 43,0.15)", color: home.membership.status === "active" ? "#32D74B" : "#D6202B" }}>
              {home.membership.status}
            </span>
          </div>
          <p className="font-heading text-2xl font-bold mt-2">{home.membership.remaining_days ?? "—"} days left</p>
          {home.membership.dues_balance > 0 && <p className="text-sm mt-1" style={{ color: "#D6202B" }}>₹{(home.membership.dues_balance / 100).toLocaleString("en-IN")} due — tap to pay</p>}
        </motion.button>
      )}

      <motion.div {...fadeUp(reducedMotion, 0.15)} className="grid grid-cols-2 gap-4">
        <button data-testid={MEMBER.actionTileBook} onClick={() => navigate("/member/book")} className="rounded-2xl p-5 flex flex-col items-start gap-2 active:scale-95 transition-transform" style={{ background: "#141414", border: "1px solid rgba(255,255,255,0.1)" }}>
          <CalendarCheck size={20} color="#D6202B" strokeWidth={1.5} />
          <span className="text-sm font-semibold">Book a class</span>
        </button>
        {home?.modules?.retail && (
          <button data-testid={MEMBER.actionTileShop} onClick={() => navigate("/member/shop")} className="rounded-2xl p-5 flex flex-col items-start gap-2 active:scale-95 transition-transform" style={{ background: "#141414", border: "1px solid rgba(255,255,255,0.1)" }}>
            <ShoppingBag size={20} color="#D6202B" strokeWidth={1.5} />
            <span className="text-sm font-semibold">Shop</span>
          </button>
        )}
      </motion.div>
    </div>
  );
}
